import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { isDemoDataSource } from '@/lib/data-source.ts'
import { clearPrivateCaches } from '@/pwa/register.ts'
import { getSupabaseClient } from '@/lib/supabase/client.ts'
import { unsubscribeClientDevice } from '@/lib/domain/clientPush.ts'

/**
 * Owner authentication.
 *
 * There is no public signup by design: an owner exists because someone created
 * an `auth.users` row and a `tenant_members` row for them. Sign-in is the only
 * entry point exposed here.
 */
export interface OwnerMembership {
  readonly tenantId: string
  readonly tenantSlug: string
  readonly tenantName: string
}

export interface OwnerSession {
  readonly userId: string
  readonly email: string
  readonly memberships: readonly OwnerMembership[]
}

export const ownerSessionKey = ['owner', 'session'] as const

async function loadSession(): Promise<OwnerSession | null> {
  const client = getSupabaseClient()

  const { data: auth } = await client.auth.getUser()
  const user = auth.user
  if (user === null) return null

  // Membership is read through RLS: the policy only returns the caller's own
  // rows, so this cannot be used to enumerate other owners.
  const { data, error } = await client
    .from('tenant_members')
    .select('tenant_id, tenants!inner(slug, name)')

  if (error !== null) throw new Error(error.message)

  const memberships = (data ?? []).map((row) => {
    const record = row as unknown as {
      tenant_id: string
      tenants: { slug: string; name: string }
    }
    return {
      tenantId: record.tenant_id,
      tenantSlug: record.tenants.slug,
      tenantName: record.tenants.name,
    }
  })

  return { userId: user.id, email: user.email ?? '', memberships }
}

export type OwnerSessionStatus = 'unavailable' | 'pending' | 'error' | 'signed-out' | 'signed-in'

export interface OwnerSessionResult {
  readonly session: OwnerSession | null
  readonly status: OwnerSessionStatus
  readonly error: Error | null
}

export function useOwnerSession(): OwnerSessionResult {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ownerSessionKey,
    queryFn: loadSession,
    enabled: !isDemoDataSource,
    retry: false,
  })

  // Supabase refreshes tokens and signs out in the background; without this the
  // cabinet would keep rendering a session that no longer exists.
  useEffect(() => {
    if (isDemoDataSource) return

    const { data } = getSupabaseClient().auth.onAuthStateChange(() => {
      void queryClient.invalidateQueries({ queryKey: ownerSessionKey })
    })

    return () => {
      data.subscription.unsubscribe()
    }
  }, [queryClient])

  if (isDemoDataSource) {
    return { session: null, status: 'unavailable', error: null }
  }

  if (query.status === 'pending') return { session: null, status: 'pending', error: null }
  if (query.status === 'error') return { session: null, status: 'error', error: query.error }

  return {
    session: query.data,
    status: query.data === null ? 'signed-out' : 'signed-in',
    error: null,
  }
}

export function useOwnerSignIn() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const { error } = await getSupabaseClient().auth.signInWithPassword(input)
      if (error !== null) throw new Error(error.message)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ownerSessionKey })
    },
  })
}

export function useOwnerSignOut() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await unsubscribeClientDevice(null)
      const { error } = await getSupabaseClient().auth.signOut()
      if (error !== null) throw new Error(error.message)
    },
    onSuccess: () => {
      // Private data must not survive a sign-out on a shared device: the
      // in-memory query cache and anything the service worker kept both go.
      queryClient.clear()
      void clearPrivateCaches()
    },
  })
}
